'use client'
import React from 'react';
import { useInvestmentPlans } from '@/lib/tenstack-hooks/useInvestmentPlans';
import { InvestmentPlansSkeletonGrid } from '@/components/ui/investment-plan-skeleton';
import { InvestmentPlan } from '@/lib/db/schema';

const Starter = () => {
  const { data: investmentPlans, isLoading, error } = useInvestmentPlans();

  if (isLoading) {
    return <InvestmentPlansSkeletonGrid />;
  }

  if (error) {
    return (
      <div className="mb-8 mt-4 px-4">
        <section className="bg-black py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-red-500">
              <p>Error loading investment plans. Please try again later.</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const formatDuration = (hours: number) => {
    if (hours < 24) {
      return `${hours} Hours`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days} Day${days > 1 ? 's' : ''}`;
    }
  };

  const formatAmount = (amount: number | null) => {
    if (amount === null) return "Unlimited";
    return `$${amount.toLocaleString()}`;
  };

  return (
    <div className='mb-8 mt-4 px-4'>
      <section className="bg-black py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {investmentPlans?.map((plan: InvestmentPlan) => (
              <div key={plan.id} className="bg-gray-800 rounded-lg shadow-lg p-6 transform hover:scale-105 transition duration-300">
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-white">{plan.name}</h3>
                  <p className="mt-4 text-gray-400">Get started with our {plan.name} Investment.</p>
                </div>
                <div className="mb-8">
                  <span className="text-5xl font-extrabold text-white">{plan.roi}%</span>
                  <span className="text-xl font-medium text-gray-400">/ROI</span>
                </div>
                <ul className="mb-8 space-y-4 text-gray-400">
                  <li className="flex items-center">
                    <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Total ROI: {plan.roi}%</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Duration: {formatDuration(plan.durationHours)}</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Minimum Deposit: {formatAmount(plan.minAmount)}</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Maximum Deposit: {formatAmount(plan.maxAmount)}</span>
                  </li>
                  {plan.instantWithdrawal && (
                    <li className="flex items-center">
                      <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Instant Withdrawal</span>
                    </li>
                  )}
                </ul>
                <a 
                  href={`/dashboard/invest?plan=${plan.id}`} 
                  className="block w-full py-3 px-6 text-center rounded-md text-white font-medium bg-gradient-to-r from-yellow-300 to-yellow-500 hover:from-yellow-400 hover:to-yellow-600 transition-all duration-300"
                >
                  Invest Now
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Starter;